import { DateTime } from "luxon"
import i18n from "i18n"
import { createSelector } from "@reduxjs/toolkit"
import { asyncAction } from "../common"
import { getState } from "storeAccess"
import { projectActions, projects, visual, message } from ".."
import { limsRequest } from "./limsRequest"
import * as limsReviewSpec from "./limsReviewSpec"
import { hasQcCapability } from "./limsCapabilities"
import { isGmpRelaxed } from "./segregationOfDuties"

// QC-task link records (PRD-003 Area 1 / SPEC-007; extended for corrections in SPEC-011). When an
// analyst sends an analysis to review, the system creates ONE real planner single-action named
// "Review <analysis>" (so it drags + IAP-schedules like any action) and a link record here tying
// that action to the analysis it covers and a creation time. SPEC-011 adds a second `kind` of
// link — a "correction" — created when a reviewer RETURNS results to the analyst: a real
// single-action named "Correct <analysis>" assigned back to the analyst's competence group(s).
// Both kinds render in the right-panel Reviews section (filtered by the viewer's role) and open
// the analysis on click. This is the "real planner action + LIMS link metadata" split already used
// for samples vs. sample-QC metadata: the action is real (POST /action/single-action) while the
// LIMS join (which action is a review/correction, which analysis it covers, who it is for, when it
// was created) is persisted via the real /lims/reviews endpoints (server-audited).
// See lims-api-contract.md.
//
// Record shape: { id, actionId, projectId, analysisId, analysisDefinitionId, analysisName,
//                 sampleIds, createdAt, kind: "review" | "correction", forMemberId? }.
//   - kind:"review"     → for the reviewer (default for legacy records with no kind).
//   - kind:"correction" → for the analyst; `forMemberId` is the enterer the correction is for.
const initialState = { items: [] }

export function reducer(state = initialState, action) {
  switch (action.type) {
    case "limsReviews/fetch":
      return { items: action.res }
    case "limsReviews/create":
      return { items: [...state.items, action.res] }
    case "auth/logout":
      return initialState
    default:
      return state
  }
}

export const fetch = asyncAction("limsReviews/fetch", (_dispatch) => {
  return limsRequest("GET", "/lims/reviews").then((res) => res.items)
})

// The set of competence group ids a member belongs to (for assigning a correction back to the
// analyst's group). Falls back to [] when the member is in no group; callers then use all groups.
function groupIdsForMember(state, memberId) {
  if (!memberId) return []
  return Object.values(state.competenceGroups || {})
    .filter((g) => Array.isArray(g.memberIds) && g.memberIds.includes(memberId))
    .map((g) => g.id)
}

// Whether a QC-task link's planner action is still OPEN (reusable) from what the FE can see locally
// (K15 dedupe). A task is open while its action is unplanned (still in the inbox) OR planned but not
// yet FINISHED — both mean the analysis has not been re-sent and a new task would just duplicate it.
// The unplanned-actions map alone is stale once a review is dragged onto the plan (fund 15), so we
// also consult the planned map. The backend remains the authoritative dedupe (see createQcTask);
// this only spares the needless create+delete round-trip when our state already shows an open link.
function isQcTaskOpen(state, actionId) {
  if (!actionId) return false
  if (state.projectActions.unplannedProjectActions[actionId]) return true
  const planned = state.projectActions.plannedProjectActions[actionId]
  return Boolean(planned && planned.stage !== "FINISHED")
}

// Shared QC-task creation: make one real planner single-action + store its LIMS link record
// (the server writes the limsAuditReviewCreated / limsAuditCorrectionCreated audit entry, keyed
// by the link's `kind`). Returns the stored link record. Used by both ensureForAnalysis
// (kind:"review") and createCorrection (kind:"correction").
//
// Server-side dedup (K15 / SPEC-008 B3): a single open QC task per (team, analysis, kind) is
// enforced by the backend. Because the FE creates the planner single-action BEFORE POSTing the
// link, the backend cannot prevent that action; instead POST /lims/reviews answers
// `{ item: <existing record>, deduped: true }` when an open link already exists. In that case the
// single-action we just created is a duplicate, so we delete its single-action project (the same
// mechanism the action editor uses to delete a single-action — projects.remove) and return the
// EXISTING record, so no second card appears. The local pre-checks in ensureForAnalysis /
// createCorrection still short-circuit when our own state already shows an open link (avoids the
// needless create+delete round-trip); this handles the race where FE state is stale (fund 15's
// root cause) and the server is the source of truth.
function createQcTask(dispatch, { name, duration, allowedGroupIds, link }) {
  return dispatch(
    projectActions.createSingleAction({
      name,
      duration,
      allowedGroupIds,
      description: "",
      tempId: "123456789",
      // Review/correction actions have no scheduling window. The backend single-action schema
      // requires these keys present (null is accepted); omitting them caused a 422.
      earliestStart: null,
      deadline: null,
    })
  ).then((res) => {
    const createdAction = res && res.project && res.project.actions && res.project.actions[0]
    const project = res && res.project
    const actionId = createdAction && createdAction.id
    const projectId = project && project.id
    const record = {
      ...link,
      actionId,
      projectId,
      createdAt: DateTime.now().toISO(),
    }
    return limsRequest("POST", "/lims/reviews", { input: record }).then((stored) => {
      // Backend deduped against an already-open QC task: drop the duplicate single-action we just
      // created and surface the existing record instead.
      if (stored && stored.deduped && projectId) {
        return dispatch(projects.remove(projectId)).then(() => stored.item)
      }
      return stored.item
    })
  })
}

// Ensure there is a review for one analysis run. Idempotent while a review is still unplanned:
//  1. Dedupe — if a review link for this analysisId already exists and its action is still
//     unplanned (in projectActions.unplannedProjectActions), return it without creating a dup.
//  2. Resolve the reviewer competence group(s) + duration from the analysis definition's Review
//     Specification (limsReviewSpec); fall back to ALL competence groups (any member) + 7200 s
//     (2 h) when none is set, so the review is always IAP-schedulable.
//  3. Create a real single-action named "Review <analysis name>" carrying those groups + duration.
//  4. Store the kind:"review" link record (POST /lims/reviews, server-audited).
//  5. Write a LIMS audit entry (F-A4).
//
// `args`: { analysis (id, name), analysisDefinitionId (may be null), sampleIds: [...] }.
export const ensureForAnalysis = asyncAction(
  "limsReviews/create",
  (dispatch, { analysis, analysisDefinitionId, sampleIds = [] }) => {
    const state = getState()
    const analysisId = analysis && analysis.id
    const analysisName = (analysis && analysis.name) || ""

    // (1) Dedupe: an existing OPEN review link for this analysis ⇒ no new review. "Open" covers an
    // unplanned action AND a planned-but-unfinished one (the inbox map goes stale once planned).
    const existing = state.limsReviews.items.find(
      (r) => r.analysisId === analysisId && (r.kind || "review") === "review" && isQcTaskOpen(state, r.actionId)
    )
    if (existing) {
      return Promise.resolve(existing)
    }

    // (2) Reviewer groups + duration from the Review Specification, else fallback. Dedupe the ids so
    // an analysis can never carry the same group twice (a stale/duplicated list must not repeat).
    const spec = analysisDefinitionId ? limsReviewSpec.selectReviewSpec(state, analysisDefinitionId) : undefined
    const specGroupIds = (spec && spec.allowedGroupIds) || []
    const allowedGroupIds = [...new Set(specGroupIds.length ? specGroupIds : Object.keys(state.competenceGroups))]
    const duration = (spec && spec.duration) || limsReviewSpec.REVIEW_FALLBACK_DURATION_SECONDS
    const name = i18n.t("limsReviewActionPrefix", { ns: "common" }) + analysisName

    // (3-5) Create the real planner single-action + link (server-audited).
    return createQcTask(dispatch, {
      name,
      duration,
      allowedGroupIds,
      link: {
        analysisId,
        analysisDefinitionId: analysisDefinitionId || null,
        analysisName,
        sampleIds,
        kind: "review",
      },
    })
  }
)

// Create a CORRECTION task when a reviewer RETURNS results to the analyst (SPEC-011). Idempotent
// while a correction for this analysis is still unplanned (same dedupe as reviews). The action is
// named "Correct <analysis>" and is assigned back to the original analyst's competence group(s)
// (from `enterer`); when the analyst is in no group, it falls back to ALL groups so it is still
// IAP-schedulable. Duration reuses the review fallback (2 h) — corrections have no spec of their
// own. The link is stored with kind:"correction" and `forMemberId`, so ReviewsSection can show it
// only to the analyst it is for.
//
// `args`: { analysis (id, name), analysisDefinitionId (may be null), sampleIds: [...],
//           enterer (memberId the correction is for) }.
export const createCorrection = asyncAction(
  "limsReviews/create",
  (dispatch, { analysis, analysisDefinitionId, sampleIds = [], enterer = null }) => {
    const state = getState()
    const analysisId = analysis && analysis.id
    const analysisName = (analysis && analysis.name) || ""

    // Dedupe: an existing OPEN correction link for this analysis (unplanned or planned-but-unfinished).
    const existing = state.limsReviews.items.find(
      (r) => r.analysisId === analysisId && r.kind === "correction" && isQcTaskOpen(state, r.actionId)
    )
    if (existing) {
      return Promise.resolve(existing)
    }

    // Assign to the analyst's competence group(s); fall back to all groups if unknown. Dedupe the ids.
    const memberGroupIds = groupIdsForMember(state, enterer)
    const allowedGroupIds = [...new Set(memberGroupIds.length ? memberGroupIds : Object.keys(state.competenceGroups))]
    const duration = limsReviewSpec.REVIEW_FALLBACK_DURATION_SECONDS
    const name = i18n.t("limsCorrectionActionPrefix", { ns: "common" }) + analysisName

    return createQcTask(dispatch, {
      name,
      duration,
      allowedGroupIds,
      link: {
        analysisId,
        analysisDefinitionId: analysisDefinitionId || null,
        analysisName,
        sampleIds,
        kind: "correction",
        forMemberId: enterer || null,
      },
    })
  }
)

// Ensure there is a SUPERVISOR review task for one analysis run (SPEC-035 / F3). Created when a
// reviewer sends results on to the supervisor (chemist) on an analysis that requires a supervisor
// review (the F0 requiresSupervisorReview flag). Mirrors ensureForAnalysis exactly — idempotent
// while still unplanned (dedupe per (team, analysis, "supervisor-review")), assigned to the Review
// Specification's competence group(s) (the supervisor's review work shares the review groups; falls
// back to ALL groups so it is always IAP-schedulable), named "Review Supervisor <analysis>". The
// link is stored with kind:"supervisor-review" (server-audited limsAuditSupervisorReviewCreated).
//
// `args`: { analysis (id, name), analysisDefinitionId (may be null), sampleIds: [...] }.
export const ensureSupervisorForAnalysis = asyncAction(
  "limsReviews/create",
  (dispatch, { analysis, analysisDefinitionId, sampleIds = [] }) => {
    const state = getState()
    const analysisId = analysis && analysis.id
    const analysisName = (analysis && analysis.name) || ""

    // Dedupe: an existing OPEN supervisor-review link for this analysis (unplanned or planned-but-unfinished).
    const existing = state.limsReviews.items.find(
      (r) => r.analysisId === analysisId && r.kind === "supervisor-review" && isQcTaskOpen(state, r.actionId)
    )
    if (existing) {
      return Promise.resolve(existing)
    }

    // Reuse the Review Specification's groups + duration (the supervisor reviews the same analysis);
    // fall back to ALL groups + the review fallback duration so the task is always schedulable.
    const spec = analysisDefinitionId ? limsReviewSpec.selectReviewSpec(state, analysisDefinitionId) : undefined
    const specGroupIds = (spec && spec.allowedGroupIds) || []
    const allowedGroupIds = [...new Set(specGroupIds.length ? specGroupIds : Object.keys(state.competenceGroups))]
    const duration = (spec && spec.duration) || limsReviewSpec.REVIEW_FALLBACK_DURATION_SECONDS
    const name = i18n.t("limsSupervisorReviewActionPrefix", { ns: "common" }) + analysisName

    return createQcTask(dispatch, {
      name,
      duration,
      allowedGroupIds,
      link: {
        analysisId,
        analysisDefinitionId: analysisDefinitionId || null,
        analysisName,
        sampleIds,
        kind: "supervisor-review",
      },
    })
  }
)

// Remove the OPEN, still-UNPLANNED QC task of a given kind for an analysis (SPEC-035 / F3). Used to
// clear a review/supervisor-review klods from the right-panel inbox when the analysis moves on to
// the next phase (send-to-supervisor clears the unplanned "review" task; supervisor-approve clears
// the unplanned "supervisor-review" task; a flag-off reviewer→QA clears the unplanned "review"
// task), so the panel does not hoard tiles. ONLY unplanned tasks are removed — a PLANNED task keeps
// its IAP placement (the user scheduled it deliberately). Deleting the single-action project
// cascades the qc_review link server-side (the FK ON DELETE CASCADE). A no-op when there is no open
// unplanned task. Returns a promise.
export const removeOpenTaskForAnalysis = (analysisId, kind) => (dispatch) => {
  const state = getState()
  const unplanned = state.projectActions.unplannedProjectActions
  const link = state.limsReviews.items.find(
    (r) => r.analysisId === analysisId && (r.kind || "review") === kind && r.projectId && unplanned[r.actionId]
  )
  if (!link) return Promise.resolve()
  return dispatch(projects.remove(link.projectId))
}

// Compare two ISO timestamps ascending (oldest first); a missing timestamp sorts as "" (earliest).
function byCreatedAtAsc(a, b) {
  return (a.createdAt || "").localeCompare(b.createdAt || "")
}

// All QC-task links (reviews + corrections) whose action is still unplanned (the inbox), oldest
// first by createdAt. A record with no `kind` is treated as a review (legacy).
export const selectOpenReviews = (state) => {
  const unplanned = state.projectActions.unplannedProjectActions
  return state.limsReviews.items.filter((r) => unplanned[r.actionId]).sort(byCreatedAtAsc)
}

// The right-panel inbox filtered to the CURRENT viewer's role (SPEC-011; SPEC-035 F3): the reviewer
// sees kind:"review" tasks; the analyst sees kind:"correction" tasks assigned to them (forMemberId
// === current member); a SUPERVISOR (holds the "supervisor" capability) sees kind:"supervisor-review"
// tasks. Legacy links without a kind count as reviews. Oldest first.
export const selectOpenQcTasksForViewer = createSelector(
  [
    (state) => state.projectActions.unplannedProjectActions,
    (state) => state.auth.memberId,
    (state) => state.limsReviews.items,
    // Whether the viewer may act as a supervisor (capability, or dev GMP-relax). Computed as an
    // input so the memoized selector recomputes when the viewer's capabilities change.
    (state) => isGmpRelaxed(state) || hasQcCapability(state, state.auth.memberId, "supervisor"),
    // RR8 #4056: the analysis tasks currently on the plan — a task whose analysis has left the plan
    // is a DEAD card (see the filter below).
    (state) => state.analysisTasks,
  ],
  (unplanned, memberId, items, isSupervisor, analysisTasks) => {
    // RR8 #4056: a card stayed in the inbox after its QA was done and the analysis had left the
    // plan; clicking it only produced "The analysis is no longer on the plan". `openReviewAnalysis`
    // already refuses in exactly that case, so the inbox now applies the SAME condition instead of
    // offering a card that cannot be opened.
    //
    // Guarded on a non-empty task map: `analysisTasks` is empty until the plan has loaded, and
    // filtering against it too early would blank the whole inbox on first render.
    const taskList = Object.values(analysisTasks || {})
    const analysisIdsOnPlan = taskList.length > 0 ? new Set(taskList.map((task) => task.analysisId)) : null
    return items
      .filter((r) => {
        if (!unplanned[r.actionId]) return false
        if (analysisIdsOnPlan !== null && !analysisIdsOnPlan.has(r.analysisId)) return false
        const kind = r.kind || "review"
        if (kind === "correction") return r.forMemberId === memberId
        // SPEC-035 (F3): supervisor-review tasks are for the supervisor(s) only. The e-signed
        // supervisor-approve still gates the action server-side; this is the inbox visibility.
        if (kind === "supervisor-review") return isSupervisor
        return true // reviews are visible to all reviewers (the analysis e-sig gates the action)
      })
      .sort(byCreatedAtAsc)
  }
)

// The set of action ids that are reviews (for intercepting a planned review tile's click).
export const selectReviewActionIds = (state) => new Set(state.limsReviews.items.map((r) => r.actionId))

// The set of project ids backing review actions (to exclude from Unplanned Projects so a review
// does not double-show there as a single-action project).
export const selectReviewProjectIds = createSelector(
  [(state) => state.limsReviews.items],
  (items) => new Set(items.map((r) => r.projectId).filter(Boolean))
)

// Open the analysis a review covers on the team plan: resolve the analysis's planned analysis task,
// jump to its week, and open its config (honoured by ScheduleElement via visual.activeAnalysisTaskId).
// If the analysis is no longer on the plan (moved out of view or removed), show a short message.
// `jumpToWeek` (RR8 #4061): move the PLAN to the task's week as well. Only meaningful for callers
// that are on (or heading to) the plan — it decides what sits BEHIND the dialog. It is not free:
// `visual.changeMondayDate` is a thunk that fires `loading.onScheduleChange()`, which flips
// `sampleView.needsRefresh`, and Sample View renders a full-screen <Loading> on that — unmounting its
// scroll container and dropping the user back at the top of the list. Callers that open the dialog
// over their own view (Sample View's "Go to analysis") pass false.
export const openReviewAnalysis = (analysisId, { jumpToWeek = true } = {}) => (dispatch) => {
  const state = getState()
  const task = Object.values(state.analysisTasks).find((t) => t.analysisId === analysisId)
  if (!task) {
    dispatch(message.info(i18n.t("limsReviewAnalysisNotFound", { ns: "common" })))
    return
  }
  if (jumpToWeek) {
    dispatch(visual.changeMondayDate(DateTime.fromISO(task.start).startOf("week").toISO()))
  }
  // SPEC-022 (K2): every openReviewAnalysis caller is a LIMS work context (review/correction tile,
  // Reviews panel card, QA worklist deep-link, Sample View fail-routing), so the task opens on the
  // Edit Analysis-task view with the "QC Results & Approvals" section expanded and in focus.
  dispatch(visual.openAnalysisTask(task.id, { focusQc: true }))
}
